import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";

const PRODUCT_TYPES = ["Rullegardin", "Plisségardin", "Lamelgardin", "Stofgardin", "Duette", "Gardinstang", "Persienne", "Andet"];

const LANGUAGE_OPTIONS = [
    { value: "da", label: "Dansk", flag: "DK" },
    { value: "en", label: "Engelsk", flag: "EN" },
];

const langLabel = (lang) => LANGUAGE_OPTIONS.find((l) => l.value === lang)?.label || "Dansk";

export default function Suppliers() {
    const { user } = useAuth();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/suppliers");
            setSuppliers(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!confirm("Slet leverandøren?")) return;
        try {
            await api.delete(`/suppliers/${id}`);
            toast.success("Leverandør slettet");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke slette");
        }
    };

    const openEdit = (s) => {
        setEditing(s);
        setOpen(true);
    };

    return (
        <div className="space-y-6" data-testid="suppliers-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">Leverandører</h1>
                    <p className="text-sm text-[#78736E] mt-1">Administrer dine faste leverandører</p>
                </div>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="add-supplier-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Ny leverandør
                </Button>
            </div>

            <div className="bg-white border border-[#E5E1DA] rounded-xl overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F4F2EF] hover:bg-[#F4F2EF] border-[#E5E1DA]">
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Navn</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Email</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Kontakt</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Sprog</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Produkttyper</TableHead>
                            <TableHead className="text-right text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Handling</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#78736E]">Indlæser...</TableCell></TableRow>}
                        {!loading && suppliers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#78736E]">Ingen leverandører endnu</TableCell></TableRow>}
                        {suppliers.map((s) => (
                            <TableRow key={s.id} className="hover:bg-[#FAFAF9] border-[#E5E1DA]" data-testid={`supplier-row-${s.id}`}>
                                <TableCell className="font-medium">{s.name}</TableCell>
                                <TableCell className="text-sm text-[#5C5854]">{s.email}</TableCell>
                                <TableCell className="text-sm text-[#5C5854]">{s.contact_person || "-"}</TableCell>
                                <TableCell className="text-sm">
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#F0EBE1] text-[#4A4641] text-xs font-medium" data-testid={`supplier-language-${s.id}`}>
                                        {langLabel(s.language)}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {s.product_types?.map((pt) => <Badge key={pt} variant="secondary" className="bg-[#F0EBE1] text-[#4A4641] border-0">{pt}</Badge>)}
                                    </div>
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    {user?.role === "admin" && (
                                        <Button variant="ghost" size="sm" onClick={() => remove(s.id)} className="text-[#993C3C]" data-testid={`delete-supplier-${s.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <SupplierDialog open={open} setOpen={setOpen} supplier={editing} onSaved={load} />
        </div>
    );
}

function SupplierDialog({ open, setOpen, supplier, onSaved }) {
    const [form, setForm] = useState({ name: "", email: "", contact_person: "", notes: "", product_types: [], language: "da" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (supplier) setForm({ name: supplier.name, email: supplier.email, contact_person: supplier.contact_person || "", notes: supplier.notes || "", product_types: supplier.product_types || [], language: supplier.language || "da" });
        else setForm({ name: "", email: "", contact_person: "", notes: "", product_types: [], language: "da" });
    }, [supplier, open]);

    const toggleType = (t) => {
        setForm((f) => ({
            ...f,
            product_types: f.product_types.includes(t) ? f.product_types.filter((x) => x !== t) : [...f.product_types, t],
        }));
    };

    const save = async () => {
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Navn og email er påkrævet");
            return;
        }
        setSaving(true);
        try {
            if (supplier) await api.patch(`/suppliers/${supplier.id}`, form);
            else await api.post("/suppliers", form);
            toast.success("Gemt");
            setOpen(false);
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading">{supplier ? "Rediger leverandør" : "Ny leverandør"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Navn *</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" />
                    </div>
                    <div>
                        <Label>Email *</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email" />
                    </div>
                    <div>
                        <Label>Kontaktperson</Label>
                        <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="supplier-contact" />
                    </div>
                    <div>
                        <Label>Sprog i emails *</Label>
                        <div className="flex gap-2 mt-1.5">
                            {LANGUAGE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setForm({ ...form, language: opt.value })}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 ${
                                        form.language === opt.value
                                            ? "bg-[#4A4641] text-white"
                                            : "bg-[#F0EBE1] text-[#5C5854] hover:bg-[#D2C9B9]"
                                    }`}
                                    data-testid={`supplier-language-${opt.value}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-[#78736E] mt-1.5">Vælger sproget for emails der sendes til denne leverandør (videresendelse + svar i tråden).</p>
                    </div>
                    <div>
                        <Label>Produkttyper</Label>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                            {PRODUCT_TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => toggleType(t)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                        form.product_types.includes(t)
                                            ? "bg-[#4A4641] text-white"
                                            : "bg-[#F0EBE1] text-[#5C5854] hover:bg-[#D2C9B9]"
                                    }`}
                                    data-testid={`supplier-type-${t}`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>Noter</Label>
                        <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="supplier-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Annuller</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-supplier-button">Gem</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
